import { getRouteStackPath } from '@portal/config/routes';
import { ThunkAction } from 'redux-thunk';
import NavigationService from '~/services/navigation';

import { pix, transfer } from '@portal/config/app.json';
import { EventType } from '@portal/enum/analytics';

import {
  DetailType,
  PixTransferType,
  TransactionType,
} from '@portal/models/enumerators';
import EmployeesApi from '@portal/repositories/employees';
import { sendEvent } from '@portal/services/firebase';
import Billet from '@portal/utils/billet';
import { Dispatch } from 'redux';
import TransactionApi from '~/repositories/transaction';
import AccountApi from '~/repositories/account';
import * as MessageService from '~/services/message';
import { LoadingActions } from '.';
import { getUserBalance } from './account';
import {
  CLEAR_TRANSACTIONS,
  EMPLOYEE_TRANSACTION_LIST,
  GET_SCHEDULED_TRANSACTIONS,
  SET_DOWNLOAD_RECEIPT_LIST,
  SET_PIX_REVERSAL_REASONS,
  TRANSACTION_GENERATE_QR_CODE,
  TRANSFER_TRANSACTIONS,
  APPEND_TRANSACTIONS,
  SET_DAILY_BALANCE,
} from './actionTypes';
import { setDetails, SetTransactionDetailAction } from './details';
import {
  decreaseExtractLoading,
  decreaseLoading,
  increaseExtractLoading,
  increaseLoading,
  LoadingAction,
} from './loading';
import { requestTotpToken } from './totpActions';
import { P2PInfo, PixReversalTransfer } from '@portal/models/bank';
import * as Sentry from '@sentry/react';

export const clearAllTransactions =
  () => async (dispatch: Dispatch<LoadingAction>) => {
    dispatch(increaseLoading());
    dispatch({
      type: CLEAR_TRANSACTIONS,
    });
    dispatch(LoadingActions.decreaseLoading());
  };

export const getAllTransactions =
  (params?: advancedFilterModels.TransactionFilter, skipLoading?: boolean) =>
  async (dispatch) => {
    !skipLoading && dispatch(increaseLoading());

    try {
      const payload = await TransactionApi.getTransactions(params || {});

      if (params?.nroUltMovimentoEnv) {
        dispatch({
          payload,
          type: APPEND_TRANSACTIONS,
        });
      } else {
        dispatch({
          payload,
          type: TRANSFER_TRANSACTIONS,
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      !skipLoading && dispatch(decreaseLoading());
    }
  };

export const getAllTransactionsDaysIn =
  (params?: advancedFilterModels.TransactionFilter) => async (dispatch) => {
    dispatch(increaseExtractLoading());

    try {
      const newParams = { ...params };
      if (params?.startAt) {
        newParams.startAt = params.startAt.split('T')[0];
      }
      if (params?.endAt) {
        newParams.endAt = params.endAt.split('T')[0];
      }

      const payload = await TransactionApi.getTransactionsDaysIn(newParams);

      if (params?.nroUltMovimentoEnv) {
        dispatch({
          payload,
          type: APPEND_TRANSACTIONS,
        });
      } else {
        dispatch({
          payload,
          type: TRANSFER_TRANSACTIONS,
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseExtractLoading());
    }
  };

export const getDailyBalance =
  (params?: advancedFilterModels.TransactionFilter) => async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const payload = await AccountApi.getDailyBalance(params || {});

      dispatch({
        payload,
        type: SET_DAILY_BALANCE,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getScheduledTransactions = () => async (dispatch) => {
  dispatch(increaseLoading());

  try {
    const payload = await TransactionApi.getScheduledTransactions();

    dispatch({
      payload,
      type: GET_SCHEDULED_TRANSACTIONS,
    });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const getPixReversalReasons = () => async (dispatch) => {
  dispatch(increaseLoading());

  try {
    const payload = await TransactionApi.getPixReversalReasons();

    dispatch({
      payload,
      type: SET_PIX_REVERSAL_REASONS,
    });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const getBilletDetails = (id: string) => async (dispatch) => {
  dispatch(increaseLoading());

  try {
    const transaction = await TransactionApi.getBilletDetails(id);
    sendEvent(EventType.READ_BILLET);

    dispatch(
      setDetails({
        transaction,
        type: DetailType.NEW,
        error: null,
      })
    );
  } catch (err) {
    if (err instanceof Error) {
      dispatch(
        setDetails({
          transaction: null,
          type: null,
          error: err,
        })
      );
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const payBillet =
  (
    billet: Billet,
    amount: number,
    transactionScheduleAt: string,
    transactionAuth: bank.TransactionAuth,
    documentType?: 'barcode' | 'typeableLine'
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const transaction = await Sentry.startSpan(
        {
          op: 'http.client',
          name: 'Pay Billet',
        },
        () =>
          TransactionApi.payBillet({
            authenticate: transactionAuth,
            transaction: {
              amount,
              document: billet.billet.barcode ?? billet.billet.typeableLine,
              documentType: documentType ?? 'barcode',
              dueDate: billet.getDueDate(),
              transactionScheduleAt: transactionScheduleAt,
            },
          })
      );

      Sentry.logger.info('Billet payment initiated', { amount });

      dispatch(
        setDetails({
          transaction,
          type: DetailType.DETAIL,
          error: null,
        })
      );

      dispatch(getUserBalance());

      if (transfer.hasSmsConfirmation) {
        dispatch(
          requestTotpToken({
            contactType: 2,
            transactionId: transaction?.id ?? '',
          })
        );
      }
    } catch (err) {
      Sentry.logger.error('Billet payment failed', {
        amount,
        message: err instanceof Error ? err.message : String(err),
      });
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const generateBillet =
  (body: models.BilletInfoRequest) => async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const transaction = await TransactionApi.generateBillet(body);
      sendEvent(EventType.GENERATE_BILLET);

      dispatch(
        setDetails({
          transaction,
          type: DetailType.NEW,
          error: null,
        })
      );
    } catch (err) {
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const sendBilletByEmail = (billetUrl: string) => async (dispatch) => {
  dispatch(increaseLoading());

  try {
    await TransactionApi.sendEmail(billetUrl);
    MessageService.success('PAGES.HOME.RECEIVE.EMAIL_SENT');
  } catch (err) {
    if (err instanceof Error) {
      dispatch(
        setDetails({
          error: err,
        })
      );
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export type SetPixDepositQrCodeAction = {
  type: string;
  payload: string;
};

export const generateQRCode =
  (
    body: models.QRCodeRequest
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction | SetPixDepositQrCodeAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const { qrCodeContent } = await TransactionApi.generateQRCode(body);
      sendEvent(EventType.GENERATE_PIX_QR_CODE);

      dispatch({
        payload: qrCodeContent,
        type: TRANSACTION_GENERATE_QR_CODE,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const pixScheduledTransfer =
  (
    pixInfo: bank.PixInfo,
    amount: number,
    transactionAuth: bank.TransactionAuth,
    transactionScheduleAt: string
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const transaction = await TransactionApi.pixScheduledTransfer({
        authenticate: transactionAuth,
        transaction: {
          amount,
          pixInfo,
          transactionScheduleAt,
          type: TransactionType.PIX,
        },
      });

      sendEvent(EventType.MAKE_TRANSACTION);

      dispatch(
        setDetails({
          transaction,
          type: DetailType.DETAIL,
          error: null,
        })
      );
      dispatch(getUserBalance());
    } catch (err) {
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const cancelPixScheduledTransfer =
  (
    id: string,
    transactionAuth: bank.TransactionAuth
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      await TransactionApi.cancelPixScheduledTransfer(id, {
        authenticate: transactionAuth,
      });

      MessageService.success('PAGES.HOME.TRANSFER.CANCEL_MESSAGE');
      NavigationService.navigate(getRouteStackPath('HOME', 'HOME_PAGE'));
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error('CANCEL_TRANSACTION_ERROR');
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const pixTransfer =
  (
    pixInfo: bank.PixInfo,
    amount: number,
    transactionAuth: bank.TransactionAuth
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const transaction = await Sentry.startSpan(
        {
          op: 'http.client',
          name: 'Pix Transfer',
        },
        () =>
          TransactionApi.pixTransfer({
            authenticate: transactionAuth,
            transaction: {
              amount,
              pixInfo: {
                ...pixInfo,
                pixTransferType: pix.sendPixTransferType
                  ? pixInfo.pixTransferType ?? PixTransferType.NORMAL
                  : undefined,
              },
            },
          })
      );

      Sentry.logger.info('Pix transfer initiated', {
        amount,
        pixKeyType: pixInfo.pixTransferType,
      });

      sendEvent(EventType.MAKE_TRANSACTION);

      dispatch(
        setDetails({
          transaction,
          type: DetailType.DETAIL,
          error: null,
        })
      );
      dispatch(getUserBalance());
    } catch (err) {
      Sentry.logger.error('Pix transfer failed', {
        amount,
        message: err instanceof Error ? err.message : String(err),
      });
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const pixWithdrawal =
  (
    pixInfo: bank.PixInfo,
    amount: number,
    transactionAuth: bank.TransactionAuth
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const transaction = await TransactionApi.pixWithdrawal({
        authenticate: transactionAuth,
        transaction: {
          amount,
          pixInfo,
        },
      });

      sendEvent(EventType.MAKE_TRANSACTION);

      dispatch(
        setDetails({
          transaction,
          type: DetailType.DETAIL,
          error: null,
        })
      );
      dispatch(getUserBalance());
    } catch (err) {
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const pixChange =
  (
    pixInfo: bank.PixInfo,
    amount: number,
    transactionAuth: bank.TransactionAuth
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const transaction = await TransactionApi.pixChange({
        authenticate: transactionAuth,
        transaction: {
          amount,
          pixInfo,
        },
      });

      sendEvent(EventType.MAKE_TRANSACTION);

      dispatch(
        setDetails({
          transaction,
          type: DetailType.DETAIL,
          error: null,
        })
      );
      dispatch(getUserBalance());
    } catch (err) {
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const pixReversalTransfer =
  ({ amount, id, pin, reasonCode, description }: PixReversalTransfer) =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      await TransactionApi.pixReversalTransfer(
        id,
        pin,
        amount,
        reasonCode,
        description
      );

      dispatch(getUserBalance());
      MessageService.success('COMPONENTS.PIX_REVERSAL_MODAL.SUCCESS');
      await NavigationService.back();
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const contactTransfer =
  (
    favoredContactId: string,
    amount: number,
    transactionAuth: bank.TransactionAuth
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const transaction = await TransactionApi.contactTransfer({
        authenticate: transactionAuth,
        transaction: {
          amount,
          favoredContactId,
        },
      });

      sendEvent(EventType.MAKE_TRANSACTION);

      dispatch(
        setDetails({
          transaction,
          type: DetailType.DETAIL,
          error: null,
        })
      );
      dispatch(getUserBalance());

      if (transfer.hasSmsConfirmation) {
        dispatch(
          requestTotpToken({
            contactType: 2,
            transactionId: transaction?.id ?? '',
          })
        );
      }
    } catch (err) {
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const pixAgencyTransfer =
  (
    amount: number,
    transactionAuth: bank.TransactionAuth,
    pixAgencyInfo: bank.PixAgencyInfo,
    pixInfo: models.PixInfo
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const transaction = await TransactionApi.pixAgencyTransfer({
        authenticate: transactionAuth,
        transaction: {
          pixAgencyInfo,
          amount,
          pixInfo,
        },
      });

      sendEvent(EventType.MAKE_TRANSACTION);

      dispatch(
        setDetails({
          transaction,
          type: DetailType.DETAIL,
          error: null,
        })
      );
      dispatch(getUserBalance());
    } catch (err) {
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const pixAgencyScheduleTransfer =
  (
    amount: number,
    transactionAuth: bank.TransactionAuth,
    pixInfo: {
      name: string;
      bank: string;
      account: string;
      accountDigit: string;
      branch: string;
      taxIdentifier: string;
      bankId: string;
      accountType: number;
    },
    transactionScheduleAt: string,
    type: number
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const transaction = await TransactionApi.pixAgencyScheduleTransfer({
        authenticate: transactionAuth,
        transaction: {
          amount,
          pixInfo,
          transactionScheduleAt,
          type,
        },
      });

      sendEvent(EventType.MAKE_TRANSACTION);

      dispatch(
        setDetails({
          transaction,
          type: DetailType.DETAIL,
          error: null,
        })
      );
      dispatch(getUserBalance());
    } catch (err) {
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const exportTransactionVoucher =
  (transactionId: string) => async (dispatch) => {
    dispatch(increaseLoading());

    try {
      await TransactionApi.exportTransactionVoucher(transactionId);
      MessageService.success(
        'PAGES.HOME.TRANSFER_CONFIRMATION.MESSAGES.SUCCESS.EMAIL_SUBMITED'
      );
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getAccountPaymentDetail =
  (id: string, params?: advancedFilterModels.PaymentDetailFilter) =>
  async (dispatch: Dispatch) => {
    dispatch(increaseLoading());

    try {
      const payload = await EmployeesApi.getAccountEmployeesPaymentDetail(
        id,
        params
      );
      dispatch({
        payload,
        type: EMPLOYEE_TRANSACTION_LIST,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const downloadReceipts = (ids: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const payload = await TransactionApi.downloadReceipts(ids);
    dispatch({
      payload,
      type: SET_DOWNLOAD_RECEIPT_LIST,
    });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const p2pScheduledTransfer =
  (
    p2pInfo: P2PInfo,
    amount: number,
    transactionAuth: bank.TransactionAuth,
    transactionScheduleAt: string,
    favoredContactId?: string
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetTransactionDetailAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());

    try {
      const transaction = await TransactionApi.p2pScheduledTransfer({
        authenticate: transactionAuth,
        transaction: {
          amount,
          p2pInfo,
          transactionScheduleAt,
        },
        favoredTransfer: {
          favoredContactId,
        },
      });

      sendEvent(EventType.MAKE_TRANSACTION);

      dispatch(
        setDetails({
          transaction,
          type: DetailType.DETAIL,
          error: null,
        })
      );
      dispatch(getUserBalance());
    } catch (err) {
      if (err instanceof Error) {
        dispatch(
          setDetails({
            error: err,
          })
        );
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const clearDownloadReceipts = () => async (dispatch) => {
  dispatch({
    type: SET_DOWNLOAD_RECEIPT_LIST,
    payload: '',
  });
};
